import { Button } from "@exhousing/design-system";
import { FileText, X } from "lucide-react";
import { useEffect, useState } from "react";

import { A4Sheet } from "@/components/document/A4Sheet";
import { RecipientChip } from "@/components/document/RecipientChip";
import {
  type RenderParams,
  type RenderedDocument,
  renderDocument,
} from "@/hooks/api/useDocuments";

/**
 * Preview the actual letter (real contract data) as A4 before committing to generate.
 * Shows who it's addressed to, then lets the user generate or back out.
 */
export function DocumentPreviewDialog({
  params,
  onClose,
  onGenerate,
  generating,
}: {
  params: RenderParams | null;
  onClose: () => void;
  onGenerate: () => void;
  generating: boolean;
}) {
  const [render, setRender] = useState<RenderedDocument | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!params) {
      setRender(null);
      return;
    }
    let active = true;
    setLoading(true);
    renderDocument(params)
      .then((r) => active && setRender(r))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [params]);

  // Close on Escape.
  useEffect(() => {
    if (!params) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [params, onClose]);

  if (!params) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black/40 backdrop-blur-sm"
      onMouseDown={onClose}
    >
      <div
        className="mx-auto flex h-full w-full max-w-4xl flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 text-white sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <span className="truncate text-sm font-medium">
              {render?.title || "Preview"}
            </span>
            {render?.recipient && <RecipientChip recipient={render.recipient} />}
          </div>
          <button
            type="button"
            aria-label="Close preview"
            onClick={onClose}
            className="rounded-md p-1.5 text-white/80 transition hover:bg-white/10 hover:text-white"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Paper */}
        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4 sm:px-6">
          <A4Sheet
            html={render?.error ? undefined : render?.html}
            loading={loading}
            error={render?.error ?? undefined}
            className="bg-transparent p-0"
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-white/10 px-4 py-3 sm:px-6">
          <Button variant="ghost" className="text-white hover:bg-white/10" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onGenerate} disabled={generating || loading || !!render?.error}>
            <FileText className="size-4" />
            {generating ? "Generating…" : "Generate this document"}
          </Button>
        </div>
      </div>
    </div>
  );
}
